import React, { FC, useMemo, useState } from "react";
import { useSetRecoilState } from "recoil";
import { hubspotPageAtom } from "../../state/atoms";
import { OutreachDisplayUser } from "../../types/users";
import FeatureAccessCell from "../../components/Addons/FeatureAccessCell";
import {
  HsBadge,
  HsCountTabs,
  HsDropdownButton,
  HsListFilterBar,
  HsListQuickFilter,
  HsMenu,
  HsAdvancedFiltersButton,
  HsSortDownIcon,
  HsSortUpIcon,
  HsTableTopBar,
  OuPill,
  OuPrimaryButton,
  OuSearch,
  OuSecondaryButton,
  OuSwitch,
  OuTable,
  OuTableColumn,
} from "../../components/HubSpotUi";
import { HubSpotSettingsPage } from "../../components/Layout/HubSpotAppShell";

export type UsersFilterType = "status" | "boringLicense" | "role" | "team";

type View = "all" | "licensed" | "unlicensed" | "deactivated";
type SortKey = "name" | "email" | "profile";

interface Props {
  /** The users after the search and filters. */
  users: OutreachDisplayUser[];
  /** Every user, for the view counts. */
  allUsers: OutreachDisplayUser[];
  isLoading: boolean;
  searchQuery: string;
  onSearch: (value: string) => void;
  /** How many values each quick filter has set (0 when it is off). */
  filterCounts: Record<UsersFilterType, number>;
  filterTypes: UsersFilterType[];
  onOpenFilter: (type: UsersFilterType, anchor: HTMLElement) => void;
  onClearFilter: (type: UsersFilterType) => void;
  onClearAll: () => void;
  onOpenAdvancedFilters: (anchor: HTMLElement) => void;
  selected: Set<string>;
  onSelect: (next: Set<string>) => void;
  onToggleLicense: (userId: string, email: string, checked: boolean) => void;
  savingIds: Set<string>;
  availableLicenses: number | null;
  activeCount: number;
  seatsAvailable: number;
  showAddLicense: boolean;
  showRemoveLicense: boolean;
  onAddLicenses: () => void;
  onRemoveLicenses: () => void;
  onRefresh: () => void;
  onSync: () => void;
  onBuy: () => void;
  onExportSelected: () => void;
}

const FILTER_LABELS: Record<UsersFilterType, string> = {
  status: "Status",
  boringLicense: "License",
  role: "Profile",
  team: "Team",
};

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const csvField = (f: string) => (/[",\n]/.test(f) ? `"${f.replace(/"/g, '""')}"` : f);

const downloadCsv = (rows: OutreachDisplayUser[]) => {
  const lines = [
    ["Name", "Email", "Teams", "Profile", "boring license", "HubSpot status"],
    ...rows.map((u) => [u.fullName, u.email, (u.teams || []).join("; "), u.role || "", u.isWhitelisted ? "Yes" : "No", u.isActive ? "Active" : "Deactivated"]),
  ];
  const blob = new Blob([lines.map((l) => l.map(csvField).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `boring-users-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * The Users tab for HubSpot orgs, as HubSpot's Settings › Users & Teams page
 * (settings-users.jpg): the H1 and boxed tabs, the description with Actions
 * and the primary pill, view tabs with counts, the filter bar, the list
 * search with its small pills, and HubSpot's table. The data, filters and
 * license writes are the Users tab's own (Users.tsx).
 */
const HubSpotUsersPage: FC<Props> = (p) => {
  const setPage = useSetRecoilState(hubspotPageAtom);
  const [view, setView] = useState<View>("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);

  const counts = useMemo(
    () => ({
      all: p.allUsers.length,
      licensed: p.allUsers.filter((u) => u.isWhitelisted).length,
      unlicensed: p.allUsers.filter((u) => !u.isWhitelisted && u.isActive).length,
      deactivated: p.allUsers.filter((u) => !u.isActive).length,
    }),
    [p.allUsers]
  );

  const rows = useMemo(() => {
    const inView = p.users.filter((u) =>
      view === "licensed" ? u.isWhitelisted : view === "unlicensed" ? !u.isWhitelisted && u.isActive : view === "deactivated" ? !u.isActive : true
    );
    if (!sort) return inView;
    const val = (u: OutreachDisplayUser) => (sort.key === "name" ? u.fullName : sort.key === "email" ? u.email : u.role || "").toLowerCase();
    return [...inView].sort((a, b) => val(a).localeCompare(val(b)) * sort.dir);
  }, [p.users, view, sort]);

  const sortHeader = (key: SortKey, label: string) => {
    const on = sort?.key === key;
    return (
      <button
        type="button"
        className="hs-sort-header"
        aria-sort={on ? (sort?.dir === 1 ? "ascending" : "descending") : undefined}
        onClick={() => setSort(on && sort?.dir === 1 ? { key, dir: -1 } : on ? null : { key, dir: 1 })}
      >
        {label}
        {on && (sort?.dir === 1 ? <HsSortUpIcon /> : <HsSortDownIcon />)}
      </button>
    );
  };

  const columns: OuTableColumn<OutreachDisplayUser>[] = [
    {
      key: "name",
      header: sortHeader("name", "Name"),
      width: 330,
      sticky: true,
      render: (u) => (
        <span className="hs-user-cell">
          <span className="hs-user-cell__avatar" aria-hidden="true">
            {u.avatarUrl ? <img src={u.avatarUrl} alt="" /> : initials(u.fullName)}
          </span>
          <span className="hs-user-cell__name">{u.fullName}</span>
          {u.role === "Super Admin" && <HsBadge tone="lavender">Super Admin</HsBadge>}
        </span>
      ),
    },
    { key: "email", header: sortHeader("email", "Email"), width: 290, render: (u) => <span className="hs-cell-text">{u.email}</span> },
    { key: "teams", header: "Teams", width: 200, render: (u) => <span className="hs-cell-text">{(u.teams || []).join(", ") || "--"}</span> },
    { key: "profile", header: sortHeader("profile", "Profile"), width: 180, render: (u) => <span className="hs-cell-text">{u.role || "--"}</span> },
    {
      key: "access",
      header: "Product access",
      width: 220,
      render: (u) => <FeatureAccessCell email={u.email} licensed={!!u.isWhitelisted} />,
    },
    {
      key: "license",
      header: "boring license",
      width: 150,
      render: (u) => {
        const id = u.id.toString();
        const canTurnOn = u.isWhitelisted || p.availableLicenses === null || p.availableLicenses - p.activeCount > 0;
        const saving = p.savingIds.has(id);
        return (
          <OuSwitch
            checked={u.isWhitelisted}
            saving={saving}
            disabled={(!canTurnOn && !u.isWhitelisted) || saving}
            aria-label={`boring license for ${u.fullName}`}
            onChange={(checked) => p.onToggleLicense(id, u.email, checked)}
          />
        );
      },
    },
    {
      key: "status",
      header: "HubSpot status",
      width: 170,
      render: (u) => <OuPill tone={u.isActive ? "success" : "neutral"}>{u.isActive ? "Active" : "Deactivated"}</OuPill>,
    },
  ];

  const anyFilter = p.filterTypes.some((t) => p.filterCounts[t] > 0);
  const licenseLine =
    p.availableLicenses !== null
      ? `${p.activeCount} of ${p.availableLicenses} boring licenses assigned, ${p.seatsAvailable} available.`
      : `${p.activeCount} boring licenses assigned.`;

  return (
    <HubSpotSettingsPage
      title="Users & Licenses"
      tabs={[
        { value: "users", label: "Users" },
        { value: "licenses", label: "License Sync" },
        { value: "billing", label: "Billing" },
      ]}
      tab="users"
      onTab={(value) => value !== "users" && setPage(value)}
      description={
        <>
          Give your team boring licenses, or sync them from HubSpot teams automatically. {licenseLine}{" "}
          <button type="button" className="hs-btn hs-btn--link" onClick={p.onSync}>
            Sync licenses
          </button>
        </>
      }
      actions={
        <>
          <HsDropdownButton use="secondary" onClick={(e) => setActionsAnchor(e.currentTarget)} aria-expanded={!!actionsAnchor}>
            Actions
          </HsDropdownButton>
          <OuPrimaryButton size="small" onClick={p.onBuy}>
            Buy licenses
          </OuPrimaryButton>
          <HsMenu
            open={!!actionsAnchor}
            anchorEl={actionsAnchor}
            onClose={() => setActionsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ style: { marginTop: 4, minWidth: 200 } }}
          >
            {[
              { label: "Sync licenses", run: p.onSync },
              { label: "Refresh users", run: p.onRefresh },
              { label: "Export all users", run: () => downloadCsv(p.allUsers) },
            ].map((a) => (
              <li key={a.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="hs-menu__item"
                  onClick={() => {
                    setActionsAnchor(null);
                    a.run();
                  }}
                >
                  {a.label}
                </button>
              </li>
            ))}
          </HsMenu>
        </>
      }
    >
      <div className="hs-users">
        <HsCountTabs
          value={view}
          onChange={(v) => setView(v as View)}
          options={[
            { value: "all", label: "All users", count: counts.all },
            { value: "licensed", label: "Licensed", count: counts.licensed },
            { value: "unlicensed", label: "Unlicensed", count: counts.unlicensed },
            { value: "deactivated", label: "Deactivated", count: counts.deactivated },
          ]}
        />
        <HsListFilterBar
          trailing={
            <button type="button" className="hs-btn hs-btn--secondary hs-btn--icon hs-focusable" aria-label="Refresh users" title="Refresh users" onClick={p.onRefresh}>
              <svg viewBox="0 0 32 32" aria-hidden="true" style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.28, strokeLinecap: "round", strokeLinejoin: "round" }}>
                <path d="M27.4 13.7A11.6 11.6 0 0 0 6 9.8M4.6 18.3A11.6 11.6 0 0 0 26 22.2" />
                <path d="M5.4 3.4v6.9h6.9M26.6 28.6v-6.9h-6.9" />
              </svg>
            </button>
          }
        >
          {p.filterTypes.map((t) => (
            <HsListQuickFilter key={t} label={FILTER_LABELS[t]} count={p.filterCounts[t]} onOpen={(el) => p.onOpenFilter(t, el)} onClear={() => p.onClearFilter(t)} />
          ))}
          {anyFilter && (
            <button type="button" className="hs-btn hs-btn--transparent hs-focusable" onClick={p.onClearAll}>
              Clear all
            </button>
          )}
          <span className="hs-list-filters__divider" aria-hidden="true" />
          <HsAdvancedFiltersButton onClick={p.onOpenAdvancedFilters} />
        </HsListFilterBar>
        <HsTableTopBar
          left={
            p.selected.size > 0 ? (
              <span className="hs-bulk">
                <strong>{p.selected.size} selected</strong>
                {p.showAddLicense && (
                  <button type="button" className="hs-btn hs-btn--transparent hs-focusable" onClick={p.onAddLicenses}>
                    Add license
                  </button>
                )}
                {p.showRemoveLicense && (
                  <button type="button" className="hs-btn hs-btn--transparent hs-focusable" onClick={p.onRemoveLicenses}>
                    Remove license
                  </button>
                )}
                <button type="button" className="hs-btn hs-btn--transparent hs-focusable" onClick={p.onExportSelected}>
                  Export
                </button>
              </span>
            ) : (
              <OuSearch variant="table" width={290} value={p.searchQuery} onChange={p.onSearch} placeholder="Search name or email address" />
            )
          }
          right={
            <>
              <OuSecondaryButton size="small" onClick={p.onSync}>
                Sync licenses
              </OuSecondaryButton>
              <OuSecondaryButton size="small" onClick={() => downloadCsv(rows)}>
                Export view
              </OuSecondaryButton>
            </>
          }
        />
        <div className="hs-table-frame">
          <OuTable<OutreachDisplayUser>
            columns={columns}
            rows={rows}
            getRowId={(u) => u.id.toString()}
            loading={p.isLoading}
            selection={{ selected: p.selected, onChange: p.onSelect }}
            pageSize={25}
          />
          {!p.isLoading && rows.length === 0 && <p className="hs-table-empty">No users match this view.</p>}
        </div>
      </div>
    </HubSpotSettingsPage>
  );
};

export default HubSpotUsersPage;
